import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Map, View, MapBrowserEvent, Feature, MapEvent } from 'ol';
import { Geometry, Point } from 'ol/geom';
import { Tile as TileLayer, VectorImage} from 'ol/layer';
import { Vector as VectorSource, XYZ, OSM } from 'ol/source';
import { fromLonLat, toLonLat, transform } from 'ol/proj';
import { getDistance } from 'ol/sphere';
import RenderFeature from 'ol/render/Feature';
import {createEmpty, extend, isEmpty} from 'ol/extent';
import { FitOptions } from 'ol/View';
import { isEqual } from 'lodash';
import {
    getRenderScale,
    createEmptyVectorSource,
    createEmptyVectorLayer,
    createSnowmobilingIconFeature,
    createCommonPathFeatures,
    createPathIconFeatures,
    createEndcapIconFeature,
    createCommonPoiIconFeature,
    createTemperaturePoiIconFeature,
    createCommonAoiIconFeature,
    createSnowmobilePathFeatures,
    createGpsIndicatorFeature,
    postMessage,
    createCommonAoiFeatures
} from './utils';
import { Style, Icon } from 'ol/style';
import { OBJECT_TYPE, OBJECT_ID, MAPBOX_ACCESS_TOKEN, MAPBOX_STYLE_USER, MAPBOX_STYLE_ID, PROPS, COLORS } from './constants';
import { FeatureType, MapType, PathCategoryId, PathDirection, PathStatus } from './enums';
import {
    MapCategories,
    MapCategoryProps,
    FeatureGroup,
} from './interfaces';
import {
    PathGpsCoordinate,
    Path,
    Paths,
    Poi,
    Pois,
    SnowmobilePaths,
    SnowmobileSubPath,
    SnowmobileSubPaths,
    GpsCoordinate,
    PathGpsCoordinates,
    PoiCategories,
    PathCategories,
    Aois,
    AoiCategories,
    AoiGpsCoordinates,
    AoiGpsCoordinate,
    Aoi
} from '@sodraberget/shared/src/interfaces';
import { getTranslatedProperty } from "../src/utils"
import './Map.css';

type MapProps = {
    style?: any;
    onMessage?: (msg: string) => void;
    onNearestFeature?: (data: any) => void;
};

const MapComponent = (props: MapProps, ref: any) => {
    const {onMessage} = props;
    //Selected language
    const selectedLanguage = useRef<string>('en');
    // Map
    const map = useRef<Map>();
    const mapRef = useRef<HTMLDivElement>(null);
    // Tile layers
    const hdMapLayer = useRef<TileLayer<OSM>>();
    const sateliteLayer = useRef<TileLayer<XYZ>>();
    // Tile sources
    const hdMapSource = useRef<OSM>();
    // Vector layers
    const snowmobilePathLayer = useRef<any>();
    const highlightedSnowmobilePathLayer = useRef<any>();
    const pathLayer = useRef<any>();
    const aoiLayer = useRef<any>();
    const highlightedAoiLayer = useRef<any>();
    const highlightedPathLayer = useRef<any>();
    const poiLayer = useRef<any>();
    const highlightedPoiLayer = useRef<any>();
    const gpsPositionLayer = useRef<any>();
    const snowmobileIconLayer = useRef<any>();
    const hikingIconLayer = useRef<any>();
    const crossCountrySkiingIconLayer = useRef<any>();
    const mountainBikingIconLayer = useRef<any>();
    const runningIconLayer = useRef<any>();
    const ridingIconLayer = useRef<any>();
    const canoeIconLayer = useRef<any>();
    const chairLiftIconLayer = useRef<any>();
    const gondolaIconLayer = useRef<any>();
    const surfaceLiftIconLayer = useRef<any>();
    const beltLiftIconLayer = useRef<any>();
    const skiSlopeIconLayer = useRef<any>();

    // Vector sources
    const aoiSource = useRef<VectorSource<Feature<Geometry>>>();
    const highlightedAoiSource = useRef<VectorSource<Feature<Geometry>>>();
    const snowmobilePathSource = useRef<VectorSource<Feature<Geometry>>>();
    const highlightedSnowmobilePathSource = useRef<VectorSource<Feature<Geometry>>>();
    const pathSource = useRef<VectorSource<Feature<Geometry>>>();
    const highlightedPathSource = useRef<VectorSource<Feature<Geometry>>>();
    const poiSource = useRef<VectorSource<Feature<Geometry>>>();
    const highlightedPoiSource = useRef<VectorSource<Feature<Geometry>>>();
    const gpsPositionSource = useRef<VectorSource<Feature<Geometry>>>();
    const snowmobileIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const hikingIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const crossCountrySkiingIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const mountainBikingIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const runningIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const ridingIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const canoeIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const chairLiftIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const gondolaIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const surfaceLiftIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const beltLiftIconSource = useRef<VectorSource<Feature<Geometry>>>();
    const skiSlopeIconSource = useRef<VectorSource<Feature<Geometry>>>();

    // Data set by calling exposed functions
    const poiCategoryData = useRef<PoiCategories>({});
    const poiData = useRef<Pois>({});
    const aoiCategoryData = useRef<AoiCategories>({});
    const aoiData = useRef<Aois>({});
    const aoiGpsData = useRef<AoiGpsCoordinates>({});
    const pathCategoryData = useRef<PathCategories>({});
    const pathData = useRef<Paths>({});
    const snowmobilePathData = useRef<SnowmobilePaths>({});
    const snowmobileSubPathData = useRef<SnowmobileSubPaths>({});
    const pathGpsData = useRef<PathGpsCoordinates>({});
    const mapType = useRef<MapType>();
    const filterData = useRef<FeatureGroup[]>();
    const focusData = useRef<FeatureGroup[]>();
    const selectedType = useRef<string | undefined>();
    const selectedId = useRef<string | undefined>();
    const highlightedType = useRef<string | undefined>();
    const highlightedId = useRef<string | undefined>();
    const gpsPositionData = useRef<GpsCoordinate>();
    const gpsIsCentered = useRef<boolean>();
    const headingData = useRef<number>();
    const pathDirection = useRef<PathDirection | undefined>();
    // Data set by internal functions
    const pathCategoryProps = useRef<MapCategoryProps>();
    const poiCategoryProps = useRef<MapCategoryProps>();
    const aoiCategoryProps = useRef<MapCategoryProps>();

    const setSelectedLanguage = (iso: string) => {
        selectedLanguage.current = iso;
    }
    
    /**
     * Exposed function to set the data for POIs
     */
    const setPois = (pois: Pois, categories: PoiCategories) => {
        if (!isEqual(poiData.current, pois) || !isEqual(poiCategoryData.current, categories)) {
            if (categories) {
                setPoiCategoryProps(categories);
            }

            poiData.current = pois;
            poiCategoryData.current = categories;
        }
    };

    /**
     * Exposed function to set the data for aois
     */
    const setAois = (aois: Aois, categories: AoiCategories, coordinates: AoiGpsCoordinates) => {
        if (!isEqual(aoiData.current, aois) || !isEqual(aoiCategoryData.current, categories) || !isEqual(aoiGpsData.current, coordinates)) {
            if (categories) {
                setAoiCategoryProps(categories);
            }
            aoiData.current = aois;
            aoiCategoryData.current = categories;
            aoiGpsData.current = coordinates;
        }
    };

    /**
     * Exposed function to set the data for paths
     */
    const setPaths = (paths: Paths, categories: PathCategories, coordinates: PathGpsCoordinates) => {
        if (!isEqual(pathData.current, paths) || !isEqual(pathCategoryData.current, categories) || !isEqual(pathGpsData.current, coordinates)) {
            if (categories) {
                setPathCategoryProps(categories);
            }

            pathData.current = paths;
            pathCategoryData.current = categories;
            pathGpsData.current = coordinates;
        }
    };

    /**
     * Exposed function to set the data for snowmobile paths
     */
    const setSnowmobilePaths = (paths: SnowmobilePaths, subPaths: SnowmobileSubPaths) => {
        if (!isEqual(snowmobilePathData.current, paths) || !isEqual(snowmobileSubPathData.current, subPaths)) {
            snowmobilePathData.current = paths;
            snowmobileSubPathData.current = subPaths;
        }
    };

    /**
     * Exposed function to set the type of map
     */
    const setMapType = (type: MapType) => {
        if (mapType.current !== type) {
            updateMapSources(type, true);

            mapType.current = type;
        }
    };
    
    /**
     * Exposed function to filter the rendered features on the map
     */
    const setFilters = (filters: FeatureGroup[]) => {
        console.log('[MAP DEBUG] setFilters() called', filters);
        if (!isEqual(filterData.current, filters)) {
            const hadFilters = filterData.current?.length;
            filterData.current = filters;

            redraw();
            // Only refocus when applying actual filters, not when clearing/initializing
            if (filters?.length) {
                refocus();
            }
        }
    };

    /**
     * Exposed function to focus on particular feature groups
     */
    const setFocus = (focus: FeatureGroup[], shouldRefocus: boolean = true) => {
        if (!isEqual(focusData.current, focus)) {
            focusData.current = focus;

            if (shouldRefocus) {
                refocus();
            }
        }
    };

    /**
     * Exposed function to set the selected feature on the map (feature to highlight and refocus on)
     */
    const setSelected = (type?: string, id?: string) => {
        selectedType.current = type;
        selectedId.current = id;

        // When deselecting (no type/id), only clear highlight without refocusing
        // This preserves the current zoom and position
        if (type === undefined && id === undefined) {
            setHighlighted(undefined, undefined);
            return;
        }

        // Highlight differs, render new highlight and then refocus
        if (highlightedType.current !== type || highlightedId.current !== id) {
            setHighlighted(type, id);
            setTimeout(() => {
                refocus();
            });
        }
        // Highlight doesn't change, just refocus
        else {
            refocus();
        }
    };

    /**
     * Exposed function to set the highlighted feature on the map
     */
    const setHighlighted = (type?: string, id?: string) => {
        //set opacity 0.5 to all unselcted features
        if (highlightedType.current !== type || highlightedId.current !== id) {
            highlightedType.current = type;
            highlightedId.current = id;
            const defaultOpacity = type !== undefined || id !== undefined ? 0.5: 1;
            //path icons
            snowmobileIconLayer.current?.setOpacity(defaultOpacity);
            snowmobileIconLayer.current?.changed();
            hikingIconLayer.current?.setOpacity(defaultOpacity);
            hikingIconLayer.current?.changed();
            mountainBikingIconLayer.current?.setOpacity(defaultOpacity);
            mountainBikingIconLayer.current?.changed();
            crossCountrySkiingIconLayer.current?.setOpacity(defaultOpacity);
            crossCountrySkiingIconLayer.current?.changed(); 
            runningIconLayer.current?.setOpacity(defaultOpacity);
            runningIconLayer.current?.changed(); 
            ridingIconLayer.current?.setOpacity(defaultOpacity);
            ridingIconLayer.current?.changed();
            canoeIconLayer.current?.setOpacity(defaultOpacity);
            canoeIconLayer.current?.changed();
            chairLiftIconLayer.current?.setOpacity(defaultOpacity);
            chairLiftIconLayer.current?.changed();
            gondolaIconLayer.current?.setOpacity(defaultOpacity);
            gondolaIconLayer.current?.changed();
            surfaceLiftIconLayer.current?.setOpacity(defaultOpacity);
            surfaceLiftIconLayer.current?.changed();
            beltLiftIconLayer.current?.setOpacity(defaultOpacity);
            beltLiftIconLayer.current?.changed();
            skiSlopeIconLayer.current?.setOpacity(defaultOpacity);
            skiSlopeIconLayer.current?.changed();
            //paths layer
            pathLayer.current?.setOpacity(defaultOpacity);
            pathLayer.current?.changed();
            snowmobilePathLayer.current?.setOpacity(defaultOpacity);
            snowmobilePathLayer.current?.changed();
            //aois layer
            aoiLayer.current?.setOpacity(defaultOpacity);
            aoiLayer.current?.changed();
            //pois layer
            poiLayer.current?.setOpacity(defaultOpacity);
            poiLayer.current?.changed();

            redrawHighlight();
        }
    };

    /**
     * Exposed function to set the current GPS position and heading on the map
     */
    const setGpsPosition = (position?: GpsCoordinate, heading?: number) => {
        if (!isEqual(gpsPositionData.current, position) || !isEqual(headingData.current, heading)) {
            gpsPositionSource.current?.clear();
          
            if (position) {
                const {longitude, latitude} = position;
                const coordinate = fromLonLat([longitude, latitude]);
                const point = new Point(coordinate);
          
                gpsPositionSource.current?.addFeatures([createGpsIndicatorFeature(point, heading)]);
                reportNearestFeature(coordinate);
            }

            // Invalidate centered state if position has changed
            if (!isEqual(gpsPositionData.current, position)) {
                gpsIsCentered.current = false;
                const msg = JSON.stringify({centered: false});
                postMessage(msg);
                onMessage?.(msg);

                gpsPositionData.current = position;
            }
        }
    };

    /**
     * Exposed function to center the view on the current GPS position
     */
    const centerGps = () => {
        if (!gpsPositionSource.current?.getFeatures().length) {
            return;
        }
        const view = map.current?.getView();
        view?.fit(gpsPositionSource.current.getExtent());
        view?.setRotation(0);

        gpsIsCentered.current = true;

        const msg = JSON.stringify({centered: true});
        postMessage(msg);
        onMessage?.(msg);
    };

    /**
     * Exposed function to zoom in or out by a specified amount
     */
    const adjustZoom = (amount: number) => {
        const view = map.current?.getView();
        if (view === undefined) {
            return;
        }

        const zoom = view.getZoom();
        if (zoom === undefined) {
            return;
        }

        view.animate({
            zoom: zoom + amount,
            duration: 100
        });
    };

    /**
     * Exposed function to set the path direction indication
     */
    const setPathDirection = (direction?: PathDirection, redraw?: boolean) => {
        pathDirection.current = direction;

        if (redraw && highlightedType.current !== undefined && highlightedId.current !== undefined) {
            redrawHighlight();
        }
    };

    // Expose functions through the component reference
    /**
     * Updates the temperature for temperature POIs
     */
    const updateTemperatures = (temperatures: Array<{id: string, temperature: number}>) => {
        if (!poiSource.current) return;
        
        temperatures.forEach(({id, temperature}) => {
            // Update POI data
            if (poiData.current[id]) {
                (poiData.current[id] as any).temperature = temperature;
            }
        });
        
        // Redraw to update display
        redraw();
    };

    useImperativeHandle(ref, () => ({
        setAois,
        setPois,
        setPaths,
        setSnowmobilePaths,
        setMapType,
        setFilters,
        setFocus,
        setSelected,
        setHighlighted,
        setGpsPosition,
        centerGps,
        adjustZoom,
        setPathDirection,
        setSelectedLanguage,
        updateTemperatures,
    }));

    // Expose functions through the window scope
    (window as any).setAois = setAois;
    (window as any).setPois = setPois;
    (window as any).setPaths = setPaths;
    (window as any).setSnowmobilePaths = setSnowmobilePaths;
    (window as any).setMapType = setMapType;
    (window as any).setFilters = setFilters;
    (window as any).setFocus = setFocus;
    (window as any).setSelected = setSelected;
    (window as any).setHighlighted = setHighlighted;
    (window as any).setGpsPosition = setGpsPosition;
    (window as any).centerGps = centerGps;
    (window as any).adjustZoom = adjustZoom;
    (window as any).setPathDirection = setPathDirection;
    (window as any).setSelectedLanguage = setSelectedLanguage;
    (window as any).updateTemperatures = updateTemperatures;

    // Initial setup
    useEffect(() => {
        // Create Standard Definition map source (lantmäteriet)
        hdMapSource.current = new XYZ({
            //url: `https://api.mapbox.com/styles/v1/${MAPBOX_STYLE_USER}/${MAPBOX_STYLE_ID}/tiles/256/{z}/{x}/{y}?access_token=${MAPBOX_ACCESS_TOKEN}`,
            // To use for retina displays (@2x) tiles för retina-displays
            url: `https://api.mapbox.com/styles/v1/${MAPBOX_STYLE_USER}/${MAPBOX_STYLE_ID}/tiles/256/{z}/{x}/{y}@2x?access_token=${MAPBOX_ACCESS_TOKEN}`,
            tilePixelRatio: 2, // important to use if you use @2x URL

            attributions: '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>',
            maxZoom: 22, // Mapbox styles only support up to 22 zoom level
            tileSize: 256, // Standard for Mapbox raster tiles.
        });

        hdMapLayer.current = new TileLayer({
            source: hdMapSource.current,
            opacity: 1.0, // Keep or adjust transparency as needed
        });
        // Set hdMapLayer to visible if the default MapType is 'Map', otherwise set its visibility to false.
        // If mapType.current is initialized as MapType.Map, make hdMapLayer visible immediately.
        hdMapLayer.current?.setVisible(mapType.current === MapType.Map);

        // Create a layer for the satelite map
        sateliteLayer.current = new TileLayer({
            source: new XYZ({
                attributions: [
                    'Powered by Esri',
                    'Source: Esri, DigitalGlobe, GeoEye, Earthstar Geographics, CNES/Airbus DS, USDA, USGS, AeroGRID, IGN, and the GIS User Community',
                ],
                attributionsCollapsible: false,
                url: `https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}`,
                maxZoom: 17,
            }),
            opacity: 0.7,
        });
        sateliteLayer.current?.setVisible(false);

        // Zoom thresholds for icon visibility
        const PATH_ICON_MIN_ZOOM = 15;
        const POI_MIN_ZOOM = 14;
        const POI_TEXT_MIN_ZOOM = 16; // POI names visible at higher zoom
        const TEMPERATURE_MIN_ZOOM = 12; // Temperature POIs visible earlier than other POIs
        const ENDCAP_MIN_ZOOM = 15; // Start/end markers visible at zoom >= 15

        // Create shared style function for path layers (handles both lines and endcap icons)
        // This function hides endcap icons when zoomed out but always shows path lines
        const createPathLayerStyleFunction = () => (feature: any, resolution: number) => {
            // Check if this feature has a __baseStyle (endcap icon)
            const baseStyle = feature.get('__baseStyle');
            
            if (baseStyle) {
                // This is an endcap icon - apply zoom threshold
                const view = map.current?.getView();
                const zoom = view?.getZoom();

                if (zoom === undefined || zoom < ENDCAP_MIN_ZOOM) {
                    return null; // Hide endcap when zoomed out
                }
                
                // Show endcap when zoomed in
                if (typeof baseStyle === 'function') {
                    try {
                        return baseStyle(resolution);
                    } catch (e) {
                        return null;
                    }
                }
                return baseStyle;
            }

            // This is a path line or other feature - check for explicit style
            const featStyle = feature.getStyle?.();
            if (featStyle) {
                if (typeof featStyle === 'function') {
                    try {
                        return featStyle(resolution);
                    } catch (e) {
                        return null;
                    }
                }
                return featStyle;
            }

            // Use default rendering
            return null;
        };

        // Create a new layers to render snowmobile paths on
        snowmobilePathSource.current = createEmptyVectorSource();
        snowmobilePathLayer.current = createEmptyVectorLayer(snowmobilePathSource.current, createPathLayerStyleFunction());
        highlightedSnowmobilePathSource.current = createEmptyVectorSource();
        highlightedSnowmobilePathLayer.current = createEmptyVectorLayer(highlightedSnowmobilePathSource.current, createPathLayerStyleFunction());

        // Create a new layers to render paths on
        pathSource.current = createEmptyVectorSource();
        pathLayer.current = createEmptyVectorLayer(pathSource.current, createPathLayerStyleFunction());
        highlightedPathSource.current = createEmptyVectorSource();
        highlightedPathLayer.current = createEmptyVectorLayer(highlightedPathSource.current, createPathLayerStyleFunction());

        // Create a new layers to render aois on
        aoiSource.current = createEmptyVectorSource();
        aoiLayer.current = createEmptyVectorLayer(aoiSource.current);
        highlightedAoiSource.current = createEmptyVectorSource();
        highlightedAoiLayer.current = createEmptyVectorLayer(highlightedAoiSource.current);

        // Create shared style function for POI layers
        // This function hides POI icons when zoomed out and renders them when zoomed in
        const createPoiStyleFunction = () => (feature: any, resolution: number) => {
            const view = map.current?.getView();
            const zoom = view?.getZoom();

            // DEBUG: Log ~1% of style calls
            const shouldLog = Math.random() < 0.01;
            if (shouldLog) {
                console.log('[POI DEBUG] Current zoom:', zoom, 'POI_MIN_ZOOM:', POI_MIN_ZOOM);
            }

            // If zoom is not yet available, don't render
            if (zoom === undefined) {
                if (shouldLog) console.log('[POI DEBUG] Zoom undefined, returning null');
                return null;
            }

            // Check if this is a temperature POI (has __isTemperature property)
            const isTemperature = feature.get('__isTemperature');
            const minZoom = isTemperature ? TEMPERATURE_MIN_ZOOM : POI_MIN_ZOOM;

            // Hide POI icons when zoomed out
            if (zoom < minZoom) {
                if (shouldLog) console.log('[POI DEBUG] Zoom', zoom, '< minZoom', minZoom, '-> hiding POI');
                return null;
            }

            if (shouldLog) console.log('[POI DEBUG] Zoom', zoom, '>= POI_MIN_ZOOM', POI_MIN_ZOOM, '-> showing POI');

            // Get the stored base style from feature property
            const baseStyle = feature.get('__baseStyle');
            if (baseStyle) {
                if (typeof baseStyle === 'function') {
                    try {
                        return baseStyle(resolution);
                    } catch (e) {
                        return null;
                    }
                }
                
                // baseStyle is now [backplate, text, icon]
                // If zoom is below POI_TEXT_MIN_ZOOM, show only backplate + icon (skip text at index 1)
                if (Array.isArray(baseStyle) && zoom < POI_TEXT_MIN_ZOOM) {
                    // Return backplate (index 0) and icon (index 2), skip text (index 1)
                    return [baseStyle[0], baseStyle[2]];
                }
                
                return baseStyle;
            }

            // Fallback: check for explicit style set via setStyle()
            const featStyle = feature.getStyle?.();
            if (featStyle) {
                if (typeof featStyle === 'function') {
                    try {
                        return featStyle(resolution);
                    } catch (e) {
                        return null;
                    }
                }
                return featStyle;
            }

            // Otherwise return null to use default rendering
            return null;
        };

        // Create a new layers to render pois on
        poiSource.current = createEmptyVectorSource();
        poiLayer.current = createEmptyVectorLayer(poiSource.current, createPoiStyleFunction());
        highlightedPoiSource.current = createEmptyVectorSource();
        highlightedPoiLayer.current = createEmptyVectorLayer(highlightedPoiSource.current, createPoiStyleFunction());

        // Create a new layer to render GPS poisition on
        gpsPositionSource.current = createEmptyVectorSource();
        gpsPositionLayer.current = createEmptyVectorLayer(gpsPositionSource.current);

        // Create shared style function for all path icon layers
        // This function hides icons when zoomed out and renders them when zoomed in
        const createPathIconStyleFunction = () => (feature: any, resolution: number) => {
            const view = map.current?.getView();
            const zoom = view?.getZoom();

            // DEBUG: Log ~1% of style calls to avoid flooding console
            const shouldLog = Math.random() < 0.01;
            if (shouldLog) {
                console.log('[PathIcon DEBUG] Current zoom:', zoom, 'PATH_ICON_MIN_ZOOM:', PATH_ICON_MIN_ZOOM);
            }

            // If zoom is not yet available, don't render
            if (zoom === undefined) {
                if (shouldLog) console.log('[PathIcon DEBUG] Zoom undefined, returning null');
                return null;
            }

            // Hide icons when zoomed out
            if (zoom < PATH_ICON_MIN_ZOOM) {
                if (shouldLog) console.log('[PathIcon DEBUG] Zoom', zoom, '< minZoom', PATH_ICON_MIN_ZOOM, '-> hiding icon');
                return null;
            }
            
            if (shouldLog) console.log('[PathIcon DEBUG] Zoom', zoom, '>= minZoom', PATH_ICON_MIN_ZOOM, '-> showing icon');

            // Otherwise build a style from icon properties placed on the feature
            const src = feature.get && (feature.get('iconSrc') || feature.get('icon'));
            if (!src) {
                if (shouldLog) console.log('[PathIcon DEBUG] No iconSrc, returning null');
                return null;
            }

            const scale = feature.get('iconScale') ?? getRenderScale() * 0.7;
            const anchor = feature.get('iconAnchor') ?? [0.5, 0.85];
            const anchorUnits = feature.get('iconAnchorUnits') ?? 'fraction';
            const opacity = feature.get('iconOpacity') ?? 1;

            return new Style({
                image: new Icon({
                    src,
                    scale,
                    anchor,
                    anchorXUnits: anchorUnits,
                    anchorYUnits: anchorUnits,
                    opacity,
                }),
            });
        };

        // Create a new layer to render snowmobile icons (no clustering) - visibility controlled by zoom
        snowmobileIconSource.current = createEmptyVectorSource();
        snowmobileIconLayer.current = createEmptyVectorLayer(snowmobileIconSource.current, createPathIconStyleFunction());

        // Create a new layer to render hiking icons (no clustering) - visibility controlled by zoom
        hikingIconSource.current = createEmptyVectorSource();
        hikingIconLayer.current = createEmptyVectorLayer(hikingIconSource.current, createPathIconStyleFunction());

        // Create a new layer to render cross country skiing icons (no clustering) - visibility controlled by zoom
        crossCountrySkiingIconSource.current = createEmptyVectorSource();
        crossCountrySkiingIconLayer.current = createEmptyVectorLayer(crossCountrySkiingIconSource.current, createPathIconStyleFunction());

        // Create a new layer to render mountain biking icons (no clustering) - visibility controlled by zoom
        mountainBikingIconSource.current = createEmptyVectorSource();
        mountainBikingIconLayer.current = createEmptyVectorLayer(mountainBikingIconSource.current, createPathIconStyleFunction());

        // Create a new layer to render running icons (no clustering) - visibility controlled by zoom
        runningIconSource.current = createEmptyVectorSource();
        runningIconLayer.current = createEmptyVectorLayer(runningIconSource.current, createPathIconStyleFunction());

        // Create a new layer to render riding icons (no clustering) - visibility controlled by zoom
        ridingIconSource.current = createEmptyVectorSource();
        ridingIconLayer.current = createEmptyVectorLayer(ridingIconSource.current, createPathIconStyleFunction());

        // Create a new layer to render canoe icons (no clustering) - visibility controlled by zoom
        canoeIconSource.current = createEmptyVectorSource();
        canoeIconLayer.current = createEmptyVectorLayer(canoeIconSource.current, createPathIconStyleFunction());

        //Create a new layer to render chair lift icons (no clustering) - visibility controlled by zoom
        chairLiftIconSource.current = createEmptyVectorSource();
        chairLiftIconLayer.current = createEmptyVectorLayer(chairLiftIconSource.current, createPathIconStyleFunction());

        //Create a new layer to render gondola icons (no clustering) - visibility controlled by zoom
        gondolaIconSource.current = createEmptyVectorSource();
        gondolaIconLayer.current = createEmptyVectorLayer(gondolaIconSource.current, createPathIconStyleFunction());

        //Create a new layer to render surface lift icons (no clustering) - visibility controlled by zoom
        surfaceLiftIconSource.current = createEmptyVectorSource();
        surfaceLiftIconLayer.current = createEmptyVectorLayer(surfaceLiftIconSource.current, createPathIconStyleFunction());

        //Create a new layer to render belt lift icons (no clustering) - visibility controlled by zoom
        beltLiftIconSource.current = createEmptyVectorSource();
        beltLiftIconLayer.current = createEmptyVectorLayer(beltLiftIconSource.current, createPathIconStyleFunction());
        
        //Create a new layer to render ski slope icons (no clustering) - visibility controlled by zoom
        skiSlopeIconSource.current = createEmptyVectorSource();
        skiSlopeIconLayer.current = createEmptyVectorLayer(skiSlopeIconSource.current, createPathIconStyleFunction());

        // Create the OL Map
        map.current = new Map({
            target: mapRef.current!,
            layers: [
                hdMapLayer.current,
                sateliteLayer.current,
                snowmobilePathLayer.current,
                pathLayer.current,
                aoiLayer.current,
                snowmobileIconLayer.current,
                hikingIconLayer.current,
                mountainBikingIconLayer.current,
                crossCountrySkiingIconLayer.current,
                runningIconLayer.current,
                ridingIconLayer.current,
                canoeIconLayer.current,
                chairLiftIconLayer.current,
                gondolaIconLayer.current,
                surfaceLiftIconLayer.current,
                beltLiftIconLayer.current,
                highlightedSnowmobilePathLayer.current,
                highlightedPathLayer.current,
                highlightedAoiLayer.current,
                poiLayer.current,
                highlightedPoiLayer.current,
                gpsPositionLayer.current,
                skiSlopeIconLayer.current,
            ],
            view: new View({
                center: fromLonLat([
                    12.493187642927492,
                    56.292703925072686
                ]),
                zoom: 14,
                minZoom: 6,
                maxZoom: 20,
            }),
            ...(process.env.REACT_APP_STANDALONE !== 'true' && {controls: []}),
        });

        // Register OL Map events
        map.current.on('singleclick', (event) => handleSingleClick(event));
        map.current.on('pointerdrag', (event) => handlePointerDrag(event));
        map.current.on('moveend', (event) => handleMoveEnd(event));

        // Expose for quick debugging in the browser console during development
        try {
            (window as any).__olMap = map.current;
        } catch (e) {
            // noop in non-browser environments
        }
    }, []);

    /**
     * Updates map sources based on map type
     */
    const updateMapSources = (type?: MapType, force?: boolean) => {
        switch (type) {
            case MapType.Map:
                sateliteLayer.current?.setVisible(false);
                hdMapLayer.current?.setVisible(true);
                break;

            case MapType.Satelite:
                hdMapLayer.current?.setVisible(false);
                sateliteLayer.current?.setVisible(true);
                break;

            default:
                break;
        }
    };

    /**
     * Handles single click events within the map
     */
     const handleSingleClick = (event: MapBrowserEvent<any>) => {
        let hitFeature:  undefined | RenderFeature | Feature<Geometry>;

        // Itterate each point and see if we hit any feature
        map.current?.forEachFeatureAtPixel(
            event.pixel,
            (rootFeature) => {
                const features = [rootFeature, ...(rootFeature.get('features') ?? [])];
                features.forEach((feature) => {
                    const type = feature.get(OBJECT_TYPE);
                    const id = feature.get(OBJECT_ID);
    
                    const isSelectable = type && id;
                    const hasPriority = !hitFeature || type === FeatureType.Poi;
    
                    if (isSelectable && hasPriority) {
                        hitFeature = feature;
                    }
                });
            },
            {
                hitTolerance: 11 * getRenderScale(),
            },
        );

        // Check if we have data within the detected feature, early return if not
        if (!hitFeature) {
            const msg = JSON.stringify({deselect: true});
            postMessage(msg);
            onMessage?.(msg);
            return;
        }

        const type = hitFeature?.get(OBJECT_TYPE);
        const id = hitFeature?.get(OBJECT_ID);

        const msg = JSON.stringify({type, id});
        postMessage(msg);
        onMessage?.(msg);
    };

    /**
     * Handles drag events within the map
     */
    const handlePointerDrag = (event: MapBrowserEvent<any>) => {
        if (gpsIsCentered.current) {
            const msg = JSON.stringify({centered: false});
            postMessage(msg);
            onMessage?.(msg);

            gpsIsCentered.current = false;
        }
    };

    /**
     * Handles move end events within the map
     */
    const handleMoveEnd = (event: MapEvent) => {
        updateMapSources(mapType.current);
    };

    /**
     * Sets up a map of the properties associated with each PoI category
     */
    const setPoiCategoryProps = (poiCategories: MapCategories) => {
        poiCategoryProps.current = {};
        for(const [key, value] of Object.entries(poiCategories)) {
            // Save Identifier data for this poi id
            const props = (PROPS.POIS as any)[value.identifier];
            if (!props) {
                console.warn(`Unknown POI category: ${value.identifier}, using default fallback`);
                poiCategoryProps.current[key] = {
                    color: COLORS.POIS.DEFAULT || '#666666',
                    icon: 'openlayers/icons/pois/poi-default.png',
                    closedIcon: 'openlayers/icons/pois/poi-default.png'
                };
            } else {
                poiCategoryProps.current[key] = props;
            }
        }
    };

    /**
     * Sets up a map of the properties associated with each Path category
     */
    const setPathCategoryProps = (pathCategories: MapCategories) => {
        pathCategoryProps.current = {};
        for(const [key, value] of Object.entries(pathCategories)) {
            // Save Identifier data for this path id
            const props = (PROPS.PATHS as any)[value.identifier];
            if (!props) {
                console.warn(`Unknown Path category: ${value.identifier}, using default fallback`);
                pathCategoryProps.current[key] = {
                    color: COLORS.PATHS.DEFAULT || '#666666',
                    icon: '',
                    closedIcon: ''
                };
            } else {
                pathCategoryProps.current[key] = props;
            }
        }
    };

    /**
     * Sets up a map of the properties associated with each Path category
     */
    const setAoiCategoryProps = (aoiCategories: MapCategories) => {
        aoiCategoryProps.current = {};
        for(const [key, value] of Object.entries(aoiCategories)) {
            // Save Identifier data for this aoi id
            const props = (PROPS.AOIS as any)[value.identifier];
            if (!props) {
                console.warn(`Unknown AOI category: ${value.identifier}, using default fallback`);
                aoiCategoryProps.current[key] = {
                    color: COLORS.AOIS.DEFAULT || '#666666',
                    icon: '',
                    closedIcon: ''
                };
            } else {
                aoiCategoryProps.current[key] = props;
            }
        }
    };

    /**
     * Redraws all the map sources given the current filters, selection, etc.
     */
    const redraw = () => {
        aoiSource.current?.clear();
        pathSource.current?.clear();
        snowmobilePathSource.current?.clear();
        poiSource.current?.clear();
        snowmobileIconSource.current?.clear();
        hikingIconSource.current?.clear();
        crossCountrySkiingIconSource.current?.clear();
        mountainBikingIconSource.current?.clear();
        runningIconSource.current?.clear();
        ridingIconSource.current?.clear();
        canoeIconSource.current?.clear();
        chairLiftIconSource.current?.clear();
        gondolaIconSource.current?.clear();
        surfaceLiftIconSource.current?.clear();
        beltLiftIconSource.current?.clear();
        skiSlopeIconSource.current?.clear();
        
        if (!filterData.current?.length) {
            drawPaths([]);
            drawSnowmobilePaths([]);
            drawPois([]);
            drawAois([]);
        } else {
            filterData.current.forEach((mapFilter: FeatureGroup) => {
                switch (mapFilter.type) {
                    case FeatureType.Path:
                        drawPaths(mapFilter.ids);
                        break;
            
                    case FeatureType.SnowmobilePath:
                        drawSnowmobilePaths(mapFilter.ids);
                        break;

                    case FeatureType.SnowmobileSubPath:
                        drawSnowmobileSubPaths(mapFilter.ids);
                        break;
            
                    case FeatureType.Poi:
                        drawPois(mapFilter.ids);
                        break;
                    
                    case FeatureType.Aoi:
                        drawAois(mapFilter.ids);
                        break;
            
                    default:
                        break;
                }
            });
        }

        if (selectedType.current !== undefined && selectedId.current !== undefined) {
            redrawHighlight();
        }
    };

    /**
     * Draws all the paths provided in pathData, filter with optional ID array
     */
    const drawPaths = (ids?: string[]) => {
        if (!pathData.current || !Object.keys(pathData.current).length) {
            return;
        }

        const crossCountrySkiingLineFeatures: Feature<Geometry>[] = [];
        const crossCountrySkiingIconFeatures: Feature<Geometry>[] = [];
        const crossCountrySkiingEndcapFeatures: Feature<Geometry>[] = [];
      
        const mountainBikingLineFeatures: Feature<Geometry>[] = [];
        const mountainBikingIconFeatures: Feature<Geometry>[] = [];
        const mountainBikingEndcapFeatures: Feature<Geometry>[] = [];
      
        const hikingLineFeatures: Feature<Geometry>[] = [];
        const hikingIconFeatures: Feature<Geometry>[] = [];
        const hikingEndcapFeatures: Feature<Geometry>[] = [];

        const runningLineFeatures: Feature<Geometry>[] = [];
        const runningIconFeatures: Feature<Geometry>[] = [];
        const runningEndcapFeatures: Feature<Geometry>[] = [];

        const ridingLineFeatures: Feature<Geometry>[] = [];
        const ridingIconFeatures: Feature<Geometry>[] = [];
        const ridingEndcapFeatures: Feature<Geometry>[] = [];

        const canoeLineFeatures: Feature<Geometry>[] = [];
        const canoeIconFeatures: Feature<Geometry>[] = [];
        const canoeEndcapFeatures: Feature<Geometry>[] = [];

        const chairLiftLineFeatures: Feature<Geometry>[] = [];
        const chairLiftIconFeatures: Feature<Geometry>[] = [];
        const chairLiftEndcapFeatures: Feature<Geometry>[] = [];

        const gondolaLineFeatures: Feature<Geometry>[] = [];
        const gondolaIconFeatures: Feature<Geometry>[] = [];
        const gondolaEndcapFeatures: Feature<Geometry>[] = [];

        const surfaceLiftLineFeatures: Feature<Geometry>[] = [];
        const surfaceLiftIconFeatures: Feature<Geometry>[] = [];
        const surfaceLiftEndcapFeatures: Feature<Geometry>[] = [];

        const beltLiftLineFeatures: Feature<Geometry>[] = [];
        const beltLiftIconFeatures: Feature<Geometry>[] = [];
        const beltLiftEndcapFeatures: Feature<Geometry>[] = [];

        const skiSlopeLineFeatures: Feature<Geometry>[] = [];
        const skiSlopeIconFeatures: Feature<Geometry>[] = [];
        const skiSlopeEndcapFeatures: Feature<Geometry>[] = [];

        // Sort paths so closed paths are rendered first (underneath open paths)
        const sortedPaths = Object.values(pathData.current).sort((a, b) => {
            const aIsClosed = a.status === PathStatus.Closed ? 0 : 1;
            const bIsClosed = b.status === PathStatus.Closed ? 0 : 1;
            return aIsClosed - bIsClosed;
        });

        sortedPaths.forEach((path) => {
            if (!ids?.length || ids.includes(path.id)) {

                switch(path.pathCategoryId) {
                    case PathCategoryId.CrossCountrySkiing:
                        drawPath(
                            crossCountrySkiingLineFeatures,
                            crossCountrySkiingIconFeatures,
                            crossCountrySkiingEndcapFeatures,
                            path,
                        );
                        break;

                    case PathCategoryId.MountainBiking:
                        drawPath(
                            mountainBikingLineFeatures,
                            mountainBikingIconFeatures,
                            mountainBikingEndcapFeatures,
                            path,
                        );
                        break;

                    case PathCategoryId.Hiking:
                        drawPath(
                            hikingLineFeatures,
                            hikingIconFeatures,
                            hikingEndcapFeatures,
                            path,
                        );
                        break;

                    case PathCategoryId.Running:
                        drawPath(
                            runningLineFeatures,
                            runningIconFeatures,
                            runningEndcapFeatures,
                            path,
                        );
                        break;

                    case PathCategoryId.Riding:
                        drawPath(
                            ridingLineFeatures,
                            ridingIconFeatures,
                            ridingEndcapFeatures,
                            path,
                        );
                        break;

                    case PathCategoryId.Canoe:
                        drawPath(
                            canoeLineFeatures,
                            canoeIconFeatures,
                            canoeEndcapFeatures,
                            path,
                        );
                        break;
                    
                    case PathCategoryId.ChairLift:
                        drawPath(
                            chairLiftLineFeatures,
                            chairLiftIconFeatures,
                            chairLiftEndcapFeatures,
                            path,
                        );
                        break;  

                    case PathCategoryId.Gondola:
                        drawPath(
                            gondolaLineFeatures,
                            gondolaIconFeatures,
                            gondolaEndcapFeatures,
                            path,   
                        );
                        break;
                    
                    case PathCategoryId.SurfaceLift:
                        drawPath(
                            surfaceLiftLineFeatures,
                            surfaceLiftIconFeatures,
                            surfaceLiftEndcapFeatures,
                            path,
                        );
                        break;
                    
                    case PathCategoryId.BeltLift:
                        drawPath(
                            beltLiftLineFeatures,
                            beltLiftIconFeatures,
                            beltLiftEndcapFeatures,
                            path,
                        );
                        break;  

                    case PathCategoryId.SkiSlope:
                        drawPath(
                            skiSlopeLineFeatures,
                            skiSlopeIconFeatures,
                            skiSlopeEndcapFeatures,
                            path,
                        );
                        break;

                    default:
                        break;
                }
            }
        });

        // Add all the features for cross country skiing
        pathSource.current?.addFeatures([
          ...crossCountrySkiingLineFeatures,
          ...crossCountrySkiingEndcapFeatures,
        ]);
        crossCountrySkiingIconSource.current?.addFeatures([...crossCountrySkiingIconFeatures]);
        // Add all the features for mountain biking
        pathSource.current?.addFeatures([
          ...mountainBikingLineFeatures,
          ...mountainBikingEndcapFeatures,
        ]);
        mountainBikingIconSource.current?.addFeatures([...mountainBikingIconFeatures]);
        // Add all the features for hiking
        pathSource.current?.addFeatures([...hikingLineFeatures, ...hikingEndcapFeatures]);
        hikingIconSource.current?.addFeatures([...hikingIconFeatures]);
        // Add all the features for running
        pathSource.current?.addFeatures([...runningLineFeatures, ...runningEndcapFeatures]);
        runningIconSource.current?.addFeatures([...runningIconFeatures]);
        // Add all the features for riding
        pathSource.current?.addFeatures([...ridingLineFeatures, ...ridingEndcapFeatures]);
        ridingIconSource.current?.addFeatures([...ridingIconFeatures]);
        // Add all the features for canoe
        pathSource.current?.addFeatures([...canoeLineFeatures, ...canoeEndcapFeatures]);
        canoeIconSource.current?.addFeatures([...canoeIconFeatures]);
        // Add all the features for chair lift
        pathSource.current?.addFeatures([...chairLiftLineFeatures, ...chairLiftEndcapFeatures]);
        chairLiftIconSource.current?.addFeatures([...chairLiftIconFeatures]);
        // Add all the features for gondola
        pathSource.current?.addFeatures([...gondolaLineFeatures, ...gondolaEndcapFeatures]);
        gondolaIconSource.current?.addFeatures([...gondolaIconFeatures]);
        // Add all the features for surface lift
        pathSource.current?.addFeatures([...surfaceLiftLineFeatures, ...surfaceLiftEndcapFeatures]);
        surfaceLiftIconSource.current?.addFeatures([...surfaceLiftIconFeatures]);
        // Add all the features for belt lift
        pathSource.current?.addFeatures([...beltLiftLineFeatures, ...beltLiftEndcapFeatures]);
        beltLiftIconSource.current?.addFeatures([...beltLiftIconFeatures]);

        // Add all the features for ski slope
        pathSource.current?.addFeatures([...skiSlopeLineFeatures, ...skiSlopeEndcapFeatures]);
        skiSlopeIconSource.current?.addFeatures([...skiSlopeIconFeatures]);
    }

    /**
     * Draws the required features for a path to the provided feature lists
     */
    const drawPath = (
        lineFeatures: Feature<Geometry>[],
        iconFeatures: Feature<Geometry>[],
        endcapFeatures: Feature<Geometry>[],
        path: Path,
        isBold: boolean = false,
        direction?: PathDirection,
    ) => {
        if (!pathCategoryProps.current) {
            return;
        }

        if (!pathGpsData.current || !pathGpsData.current[path.id]) {
            return;
        }
        const { difficulty, pathCategoryId } = path;
        
        // Get the difficulty color for the ski slope path, use cross-country skiing color for all other paths
        const color = pathCategoryId === PathCategoryId.SkiSlope
            ? PROPS.PATHS.DIFFICULTY[difficulty]
            : COLORS.PATHS.CROSS_COUNTRY_SKIING;

        const {icon, closedIcon} = pathCategoryProps.current[path.pathCategoryId];
        const lineCoordinates = pathGpsData.current[path.id].map((coordinate: PathGpsCoordinate, index: number) =>
            fromLonLat([coordinate.longitude, coordinate.latitude, index]),
        );
        
        // Skip if no coordinates
        if (!lineCoordinates || lineCoordinates.length === 0) {
            return;
        }
        
        // Get status directly from path object (it's an enum, not a translated property)
        const pathStatus = path.status;
        const iconUrl = pathStatus !== PathStatus.Closed ? icon : closedIcon;
        
        // Add line features (path and arrows)
        lineFeatures.push(
            ...createCommonPathFeatures(
                lineCoordinates,
                path.id,
                color,
                pathStatus === PathStatus.Closed,
                isBold,
                direction,
            ),
        );
        
        // Add path type icons along the trail (always show, even for closed paths)
        const iconOpacity = pathStatus === PathStatus.Closed ? 0.5 : 1;
        iconFeatures.push(...createPathIconFeatures(lineCoordinates, iconUrl, color, 1000, iconOpacity));

        const endIndex = lineCoordinates.length - 1;
      
        const hasDarkBackground = mapType.current === MapType.Satelite;
        const startName = getTranslatedProperty(selectedLanguage?.current, 'start', path)
        const endName = getTranslatedProperty(selectedLanguage?.current, 'end', path)
        const endcapOpacity = pathStatus === PathStatus.Closed ? 0.3 : 1;
        endcapFeatures.push(createEndcapIconFeature(new Point(lineCoordinates[0]), startName, hasDarkBackground, color, endcapOpacity));
        endcapFeatures.push(createEndcapIconFeature(new Point(lineCoordinates[endIndex]), endName, hasDarkBackground, color, endcapOpacity));
    };

    /**
     * Draws all the paths provided in snowmobilePathData, filter with optional ID array
     */
    const drawSnowmobilePaths = (ids?: string[]) => {
        const lineFeatures: Feature<Geometry>[] = [];
        const iconFeatures: Feature<Geometry>[] = [];

        if (ids?.length) {
            ids.forEach((id) => {
                const path = snowmobilePathData.current[id];
        
                path.subPaths.forEach((subPath) => {
                    if (typeof subPath === 'string') {
                        drawSnowmobileSubPath(lineFeatures, iconFeatures, snowmobileSubPathData.current[subPath]);
                    }
                    else {
                        drawSnowmobileSubPath(lineFeatures, iconFeatures, subPath);
                    }
                });
            });
        } else {
            Object.values(snowmobileSubPathData.current).forEach((subPath) => {
                drawSnowmobileSubPath(lineFeatures, iconFeatures, subPath);
            });
        }
      
        snowmobilePathSource.current?.addFeatures([...lineFeatures, ...iconFeatures]);
        snowmobileIconSource.current?.addFeatures([]); // NOTE: Empty at the moment as many paths icons for crossings overlap, resulting in always being clustered
    };

    /**
     * Draws all the subpaths provided in snowmobileSubPathData, filter with optional ID array
     */
    const drawSnowmobileSubPaths = (ids?: string[]) => {
        const lineFeatures: Feature<Geometry>[] = [];
        const iconFeatures: Feature<Geometry>[] = [];

        if (ids?.length) {
            Object.values(snowmobileSubPathData.current).forEach((subPath) => {
                if (ids.includes(subPath.id)) {
                    drawSnowmobileSubPath(lineFeatures, iconFeatures, subPath);
                }
            });
        }
      
        snowmobilePathSource.current?.addFeatures([...lineFeatures, ...iconFeatures]);
        snowmobileIconSource.current?.addFeatures([]); // NOTE: Empty at the moment as many paths icons for crossings overlap, resulting in always being clustered
    };

    /**
     * Draws the required features for a snowmobile subpath to the provided feature lists
     */
    const drawSnowmobileSubPath = (
        lineFeatures: Feature<Geometry>[],
        iconFeatures: Feature<Geometry>[],
        subPath: SnowmobileSubPath,
        isBold: boolean = false,
    ) => {
        const lineCoordinates = subPath.subPathGPSCoordinates.map((coordinate) =>
            fromLonLat([coordinate.longitude, coordinate.latitude]),
        );
        
        // Skip if no coordinates
        if (!lineCoordinates || lineCoordinates.length === 0) {
            return;
        }
      
        lineFeatures.push(
            ...createSnowmobilePathFeatures(
                lineCoordinates,
                subPath.id,
                subPath.status === PathStatus.Closed,
                isBold,
            ),
        );
      
        if (subPath.start) {
            iconFeatures.push(
                createSnowmobilingIconFeature(new Point(lineCoordinates[0]), subPath.start, isBold),
            );
        }
      
        if (subPath.end) {
            const endIndex = lineCoordinates.length - 1;
            iconFeatures.push(
                createSnowmobilingIconFeature(new Point(lineCoordinates[endIndex]), subPath.end, isBold),
            );
        }
    };

    /**
     * Draws all the POIs provided in poiData, filter with optional ID array
     */
    const drawPois = (ids?: string[]) => {
        const iconFeatures: Feature<Geometry>[] = [];

        Object.values(poiData.current).forEach((poi) => {
            if (!ids?.length || ids.includes(poi.id)) {
                drawPoi(iconFeatures, poi);
            }
        });
        
        poiSource.current?.addFeatures([...iconFeatures]);
    };

    /**
     * Draws the required features for a POI to the provideed feature lists
     */
    const drawPoi = (iconFeatures: Feature<Geometry>[], poi: Poi, isBold: boolean = false) => {
        if (!poiCategoryProps.current) {
            return;
        }
        
        // Skip unknown POI categories gracefully
        const categoryProps = poiCategoryProps.current[poi.poiCategoryId];
        if (!categoryProps) {
            console.warn(`Skipping POI ${poi.id}: unknown category ${poi.poiCategoryId}`);
            return;
        }
        
        const {color, icon} = categoryProps;
        const coordinates = fromLonLat([poi.longitude, poi.latitude]);
      
        const hasDarkBackground = mapType.current === MapType.Satelite;
        
        // Check if this is a temperature POI (POI_CATEGORY_19)
        // poiCategoryId is a string after conversion from API response
        if (poi.poiCategoryId === '19') {
            // Extract temperature from POI description
            // Description might be a string (JSON) or an object
            let temperature = 0;
            
            if (typeof poi.description === 'string') {
                try {
                    const parsed = JSON.parse(poi.description);
                    temperature = parsed?.temperature ?? 0;
                } catch (e) {
                    temperature = 0;
                }
            } else if (typeof poi.description === 'object' && poi.description !== null) {
                temperature = (poi.description as any)?.temperature ?? 0;
            }
            
            const tempFeature = createTemperaturePoiIconFeature(new Point(coordinates), poi.id, temperature, color, icon, hasDarkBackground, isBold);
            tempFeature.set('__isTemperature', true); // Mark as temperature POI for zoom handling
            iconFeatures.push(tempFeature);
        } else {
            const name = getTranslatedProperty(selectedLanguage?.current, 'name', poi)
            iconFeatures.push(
                createCommonPoiIconFeature(new Point(coordinates), poi.id, name, color, icon, hasDarkBackground, isBold),
            );
        }
    };

    /**
     * Draws all the AOIs provided in aoiData, filter with optional ID array
     */
    const drawAois = (ids?: string[]) => {
        const iconFeatures: Feature<Geometry>[] = [];
        const lineFeatures: Feature<Geometry>[] = [];    

        Object.values(aoiData.current).forEach((aoi) => {
            if (!ids?.length || ids.includes(aoi.id)) {
                drawAoi(lineFeatures, iconFeatures, aoi);
            }
        });
        
        aoiSource.current?.addFeatures([...iconFeatures]);
    };

    /**
     * Draws the required features for a aoi to the provided feature lists
     */
    const drawAoi = (
        lineFeatures: Feature<Geometry>[],
        iconFeatures: Feature<Geometry>[],
        aoi: Aoi,
        isBold: boolean = false,
    ) => {

        if (!aoiCategoryProps.current) {
            return;
        }

        if (!aoiGpsData.current || !aoiGpsData.current[aoi.id]) {
            return;
        }
        
        // Skip unknown AOI categories gracefully
        const categoryProps = aoiCategoryProps.current[aoi.aoiCategoryId];
        if (!categoryProps) {
            console.warn(`Skipping AOI ${aoi.id}: unknown category ${aoi.aoiCategoryId}`);
            return;
        }

        const {color, icon} = categoryProps;
        const lineCoordinates = aoiGpsData.current[aoi.id].map((coordinate: AoiGpsCoordinate, index: number) =>
            fromLonLat([coordinate.longitude, coordinate.latitude, index]),
        );
     
        lineFeatures.push(
            ...createCommonAoiFeatures(
                lineCoordinates,
                aoi.id,
                color,
            ),
        );

        const xCoordinates = lineCoordinates.map((coordinate) => coordinate[0]);
        xCoordinates.sort((a, b) => a - b);
        const yCoordinates = lineCoordinates.map((coordinate) => coordinate[1]);
        yCoordinates.sort((a, b) => a - b);
        const center = [
          (xCoordinates[0] + xCoordinates[xCoordinates.length - 1]) / 2,
          (yCoordinates[yCoordinates.length - 1] + yCoordinates[0]) / 2,
        ];

        const hasDarkBackground = mapType.current === MapType.Satelite;
        const name = getTranslatedProperty(selectedLanguage?.current, 'name', aoi);
        iconFeatures.push(
            createCommonAoiIconFeature(new Point(center), aoi.id, name, color, icon, hasDarkBackground, isBold),
        );
    };

    /**
     * Draws the required features for the current selection to the highlight source
     */
    const redrawHighlight = () => {
        const lineFeatures: Feature<Geometry>[] = [];
        const iconFeatures: Feature<Geometry>[] = [];

        highlightedPathSource.current?.clear();
        highlightedSnowmobilePathSource.current?.clear();
        highlightedPoiSource.current?.clear();
        highlightedAoiSource.current?.clear();

        highlightedPathLayer.current?.setVisible(false);
        highlightedSnowmobilePathLayer.current?.setVisible(false);
        highlightedPoiLayer.current?.setVisible(false);
        highlightedAoiLayer.current?.setVisible(false);
        
        if (!highlightedId.current) {
            return;
        }
      
        switch (highlightedType.current) {
            case FeatureType.Path:
                const path = pathData.current[`${highlightedId.current}`];
                drawPath(lineFeatures, iconFeatures, iconFeatures, path, true, pathDirection.current);
                highlightedPathSource.current?.addFeatures([...lineFeatures, ...iconFeatures]);
                highlightedPathLayer.current?.setVisible(true);
                break;
        
            case FeatureType.SnowmobilePath:
                const snowmobilePath = snowmobilePathData.current[`${highlightedId.current}`];
                snowmobilePath.subPaths.forEach((subPath) => {
                    if (typeof subPath === 'string') {
                        drawSnowmobileSubPath(lineFeatures, iconFeatures, snowmobileSubPathData.current[subPath], true);
                    }
                    else {
                        drawSnowmobileSubPath(lineFeatures, iconFeatures, subPath);
                    }
                });
                highlightedSnowmobilePathSource.current?.addFeatures([...lineFeatures, ...iconFeatures]);
                highlightedSnowmobilePathLayer.current?.setVisible(true);
                break;
        
            case FeatureType.SnowmobileSubPath:
                const snowmobileSubPath = snowmobileSubPathData.current[highlightedId.current];
                drawSnowmobileSubPath(
                    lineFeatures,
                    iconFeatures,
                    snowmobileSubPath,
                    true,
                );
                highlightedSnowmobilePathSource.current?.addFeatures([...lineFeatures, ...iconFeatures]);
                highlightedSnowmobilePathLayer.current?.setVisible(true);
                break;
        
            case FeatureType.Poi:
                const poi = poiData.current[`${highlightedId.current}`];
                drawPoi(iconFeatures, poi, true);
                highlightedPoiSource.current?.addFeatures(iconFeatures);
                highlightedPoiLayer.current?.setVisible(true);
                break;

            case FeatureType.Aoi:
                const aoi = aoiData.current[`${highlightedId.current}`];
                drawAoi(lineFeatures, iconFeatures, aoi, true);
                highlightedAoiSource.current?.addFeatures([...lineFeatures, ...iconFeatures]);
                highlightedAoiLayer.current?.setVisible(true);
                break;
        
            default:
                break;
        }
    };

    /**
     * Recenters the selected feature, recenters all content if no selection is provided
     */
    const refocus = () => {
        console.log('[MAP DEBUG] refocus() called', {
            selectedType: selectedType.current,
            selectedId: selectedId.current,
            focusData: focusData.current,
            filterData: filterData.current,
            stack: new Error().stack?.split('\n').slice(1, 5).join('\n')
        });
        const source = getSourceForType(selectedType.current);
        const POI_MIN_ZOOM = 14; // Same as defined for POI visibility
        
        // Check if we're focusing on POIs (from filtered list "show on map")
        // Check both focusData and filterData since filters are used for "show on map"
        const dataToCheck = focusData.current?.length ? focusData.current : filterData.current;
        const hasPois = dataToCheck?.some(group => group.type === FeatureType.Poi);
        
        const options: FitOptions = {
            padding: [50, 50, 50, 50],
            maxZoom: selectedType.current === FeatureType.Poi ? 18 : undefined,
        };

        if (source?.getFeatures().length) {
            if (selectedId.current !== undefined) {
                let extent = createEmpty();
        
                if (selectedType.current === FeatureType.SnowmobilePath) {
                    const subPathIds = snowmobilePathData.current[selectedId.current]?.subPaths;
            
                    if (subPathIds?.length) {
                        source.getFeatures().forEach((feature) => {
                            subPathIds.forEach((subPathId) => {
                                const type = feature.get(OBJECT_TYPE);
                                const id = feature.get(OBJECT_ID);

                                if (type === FeatureType.SnowmobileSubPath && id === subPathId) {
                                    extend(extent, feature!.getGeometry()!.getExtent());
                                }
                            });
                        });
                    }
                } else {
                    source.getFeatures().forEach((feature) => {
                        const type = feature.get(OBJECT_TYPE);
                        const id = feature.get(OBJECT_ID);
                    
                        if (type === selectedType.current && id === selectedId.current) {
                            extend(extent, feature!.getGeometry()!.getExtent());
                        }
                    });
                }

                map.current?.getView().fit(extent, options);
            } else {
                const extent = source.getExtent();
                map.current?.getView().fit(extent, options);
            }
        } else {
            const extent = createEmpty();
            if (pathSource.current && snowmobilePathSource.current && poiSource.current && aoiSource.current) {
                // Use focusData if available, otherwise use filterData
                const dataToUse = focusData.current?.length ? focusData.current : filterData.current;
                
                if (dataToUse?.length) {
                    dataToUse.forEach((group) => {
                        const {type: groupType, ids} = group;
                        const groupSource = getSourceForType(groupType);

                        groupSource?.getFeatures().forEach((feature) => {
                            const type = feature.get(OBJECT_TYPE);
                            const id = feature.get(OBJECT_ID);
                        
                            if (type === groupType && ids.includes(id)) {
                                extend(extent, feature!.getGeometry()!.getExtent());
                            }
                        });
                    });
                }
                else {
                    extend(extent, pathSource.current.getExtent());
                    extend(extent, snowmobilePathSource.current.getExtent());
                    extend(extent, poiSource.current.getExtent());
                    extend(extent, aoiSource.current.getExtent());
                }
            }
            if (!isEmpty(extent)) {
                map.current?.getView().fit(extent, options);
            }
        }
        
        // If we're focusing on POIs and zoom is too far out, adjust to POI_MIN_ZOOM
        // This ensures POIs are always visible after "show on map"
        if (hasPois) {
            setTimeout(() => {
                const view = map.current?.getView();
                const currentZoom = view?.getZoom();
                if (currentZoom !== undefined && currentZoom < POI_MIN_ZOOM) {
                    view?.setZoom(POI_MIN_ZOOM);
                }
            }, 100);
        }
    };

    /**
     * Gets the source for a given feature type
     */
    const getSourceForType = (type?: string): VectorSource<Feature<Geometry>> | undefined => {
        switch (type) {
            case FeatureType.Path:
                return pathSource.current;
        
            case FeatureType.SnowmobilePath:
            case FeatureType.SnowmobileSubPath:
                return snowmobilePathSource.current;
        
            case FeatureType.Poi:
                return poiSource.current;

            case FeatureType.Aoi:
                return aoiSource.current;
        
            default:
                return undefined;
        }
    };

    /**
     * Gets the distance between two coordinates
     */
    const getCoordsDistance = (coordinate1: number[], coordinate2: number[], projection?: string): number => {
        projection = projection || 'EPSG:4326';
    
        const sourceProj = map.current?.getView().getProjection();
        const c1 = transform(coordinate1, sourceProj, projection);
        const c2 = transform(coordinate2, sourceProj, projection);
    
        return getDistance(c1, c2);
    };

    /**
     * Posts a message about the feature nearest to a GPS coordinate
     */
    const reportNearestFeature = (coordinate: number[]) => {
        const closestFeature = pathSource.current?.getClosestFeatureToCoordinate(coordinate);

        if (closestFeature) {
            const closestPoint = closestFeature!
                .getGeometry()!
                .getClosestPoint(coordinate);

            if (getCoordsDistance(closestPoint, coordinate) > 70) {
                return;
            }

            const objectId = closestFeature.get(OBJECT_ID);
            if (!objectId) {
                return;
            }

            const latLongCoords = toLonLat(closestPoint);
            const msg = JSON.stringify({
                pathId: parseInt(objectId, 10),
                pointCoordinate: {
                    x: latLongCoords[0],
                    y: latLongCoords[1],
                    ...(closestPoint[2] && {index: closestPoint[2]}),
                },
            });
            postMessage(msg);
            onMessage?.(msg);
        } else {
            const msg = JSON.stringify({
                pathId: 0,
                pointCoordinate: {
                    x: 0,
                    y: 0,
                },
            });
            postMessage(msg);
            onMessage?.(msg);
        }
    };

    return (
        <div ref={mapRef} className="map"></div>
    )
};

export default forwardRef(MapComponent);